import createFloating from "@corvu/utils/create/floating";
import { getFloatingStyle } from "@corvu/utils/floating";
import { matchSorter } from "match-sorter";
import { createSignal, For, type JSX, Show } from "solid-js";
import { unwrap } from "solid-js/store";

import Modal from "~/components/Modal";
import { type RadioShowInfo, usePlayer } from "~/components/PlayerContext";
import Image from "~/components/player/Image";
import rootStyles from "~/components/Root.module.scss";
import styles from "~/components/SongRequestModal.module.scss";

interface Title {
  album: string;
  artist: string;
  title: string;
  cover: string;
}

export default function SongRequestModal() {
  const player = usePlayer();
  const [formRef, setFormRef] = createSignal<HTMLFormElement>();
  const [nameRef, setNameRef] = createSignal<HTMLInputElement>();
  const [fileRef, setFileRef] = createSignal<HTMLInputElement | null>(null);
  const [fileMatchesRef, setFileMatchesRef] =
    createSignal<HTMLDivElement | null>(null);
  const [messageRef, setMessageRef] = createSignal<HTMLTextAreaElement>();
  const [mode, setMode] = createSignal<RadioShowInfo["type"]>("playlist");
  const [sending, setSending] = createSignal(false);

  const floatingState = createFloating({
    reference: fileRef,
    floating: fileMatchesRef,
    strategy: "fixed",
    options: {
      flip: true,
      size: {
        matchSize: true,
        fitViewPort: true,
      },
    },
  });
  const floatingStyle = getFloatingStyle({ strategy: "fixed", floatingState });

  let availableSongs: Record<string, Title> = {};
  const songs = () => Object.values(availableSongs);
  const titles = () =>
    Object.fromEntries(
      Object.keys(availableSongs).map((key) => [
        formatTitle(availableSongs[key]),
        key,
      ]),
    );
  const [matches, setMatches] = createSignal<Title[]>([]);

  const onShow = () => {
    const name = nameRef();
    const file = fileRef();
    const message = messageRef();

    if (name) {
      name.value = "";
    }

    if (file) {
      file.value = "";
    }

    if (message) {
      message.value = "";
    }

    const radioShowType = unwrap(player).metadata?.program.type ?? "live";

    availableSongs = {};
    setMode(radioShowType);

    if (radioShowType !== "live") {
      setSending(true);
      fetch(
        `${import.meta.env.VITE_STREAMING_API_ENDPOINT}/song-request?minimal=true`,
      )
        .then((response) => response.json())
        .then((response) => {
          availableSongs = response;
        })
        .catch((error: unknown) => {
          console.error(error);
        })
        .finally(() => {
          setSending(false);
        });
    }
  };

  const formatTitle = (value: Title) =>
    [value.album, value.artist, value.title].filter(Boolean).join(" - ");

  const doMatch = (force?: boolean) => {
    const file = fileRef();
    const fileMatches = fileMatchesRef();
    if (!file || !fileMatches) {
      return;
    }

    const matches = matchSorter(songs(), file.value, { keys: [formatTitle] });
    setMatches(matches);

    if (force || file.value !== "") {
      fileMatches.showPopover();
    }
  };

  const closeFileMatches = () => {
    const fileMatches = fileMatchesRef();
    const file = fileRef();
    if (!fileMatches || !file) {
      return;
    }

    fileMatches.hidePopover();

    if (!(file.value in titles())) {
      file.value = "";
    }
  };

  const setValue = (value: string) => {
    const file = fileRef();
    const fileMatches = fileMatchesRef();
    if (!file || !fileMatches) {
      return;
    }

    file.value = value;
    fileMatches.hidePopover();
    file.focus();
  };

  const sendRequest: JSX.EventHandlerUnion<HTMLFormElement, SubmitEvent> = (
    event,
  ) => {
    event.preventDefault();
    const request = new XMLHttpRequest();
    request.addEventListener("load", () => {
      if (mode() === "podcast") {
        return;
      }

      setSending(false);

      let message = "";
      if (request.status >= 400) {
        switch (request.status) {
          case 400: {
            switch (request.responseText) {
              case "request.required_fields": {
                message = "Preencha seu nome e a música pedida.";
                break;
              }

              default: {
                message = "Erro desconhecido.";
              }
            }

            break;
          }

          case 403: {
            switch (request.responseText) {
              case "request.must_be_playlist": {
                message =
                  "Não é possível fazer pedidos offline em podcasts ou programas ao vivo.";
                break;
              }

              case "request.duplicate": {
                message = "Essa música já foi tocada ou pedida recentemente.";
                break;
              }

              default: {
                message = "Você está impedido(a) de fazer pedidos...";
              }
            }

            break;
          }

          case 404: {
            message = "Essa música não existe ou não é desta playlist.";
            break;
          }

          case 429: {
            message = "Você já fez pedidos demais por ora. Aguarde um pouco.";
            break;
          }

          default: {
            message = "Erro desconhecido.";
          }
        }
      }

      if (request.status >= 500) {
        message = "Erro desconhecido.";
      }

      if (message) {
        alert(message);
        return;
      }

      alert(
        mode() === "live"
          ? "Pedido enviado com sucesso. Cabe exclusivamente ao DJ no ar atendê-lo ou não."
          : "Pedido enviado com sucesso! Aguarde um pouco.",
      );
      history.back();
    });
    request.addEventListener("error", () => {
      setSending(false);
      alert("Erro desconhecido.");
    });

    let data: FormData;

    if (mode() === "live") {
      const originalData = new FormData(formRef());
      data = new FormData();
      data.append("nome", originalData.get("name") as string);
      data.append("pedido", originalData.get("file") as string);
      data.append("recado", originalData.get("message") as string);
    } else if (mode() === "playlist") {
      data = new FormData(formRef());
      data.set("file", titles()[fileRef()?.value ?? ""]);
    }

    request.open(
      formRef()?.method ?? "post",
      mode() === "live"
        ? `${import.meta.env.VITE_API_ENDPOINT}/song-request`
        : `${import.meta.env.VITE_STREAMING_API_ENDPOINT}/song-request`,
    );
    // @ts-expect-error: this works because we're not sending any blobs here
    request.send(new URLSearchParams(data));
    setSending(true);
  };

  return (
    <Modal id="song-requests" title="Pedir música" onShow={onShow}>
      <form ref={setFormRef} method="post" onSubmit={sendRequest}>
        <fieldset
          class={styles.wrapper}
          disabled={mode() === "podcast" || sending()}
        >
          <p>
            {mode() === "live"
              ? "Interaja com o DJ enviando-lhe um pedido musical e/ou um recado."
              : mode() === "podcast"
                ? "Não é possível fazer pedidos a um podcast."
                : "Faça um pedido musical dentro da temática da playlist."}
          </p>
          <div>
            <label for="sr-name">Nome ou apelido *</label>
            <input
              ref={setNameRef}
              required
              id="sr-name"
              name="name"
              minLength={3}
            />
          </div>
          <div class={styles.file}>
            <label for="sr-file">
              Música pedida{mode() === "playlist" ? " *" : ""}
            </label>
            <input
              ref={setFileRef}
              required={mode() === "playlist"}
              id="sr-file"
              name="file"
              {...{
                minLength: mode() === "live" ? 8 : undefined,
                onInput: () => {
                  if (mode() !== "playlist") {
                    return;
                  }

                  doMatch();
                  fileMatchesRef()?.scrollTo(0, 0);
                },
                onKeyDown: (event) => {
                  if (mode() !== "playlist") {
                    return;
                  }

                  const file = fileRef();
                  const fileMatches = fileMatchesRef();
                  if (!file || !fileMatches) {
                    return;
                  }

                  if (event.key === "Escape") {
                    if (file.value || fileMatches.matches(":popover-open")) {
                      event.preventDefault();
                      file.value = "";
                    }

                    closeFileMatches();
                    return;
                  }

                  if (event.key === "Tab") {
                    closeFileMatches();
                    return;
                  }

                  if (event.key === "ArrowDown") {
                    event.preventDefault();
                    doMatch(true);
                    fileMatches.querySelector("div")?.focus();
                  }
                },
                onFocusOut: (event) => {
                  if (mode() !== "playlist") {
                    return;
                  }

                  if (
                    !(event.relatedTarget as HTMLElement | null)?.closest(
                      "#sr-file-matches",
                    )
                  ) {
                    closeFileMatches();
                  }
                },
              }}
            />
            {/* biome-ignore lint/a11y/noStaticElementInteractions: using event delegation */}
            <div
              ref={setFileMatchesRef}
              popover
              id="sr-file-matches"
              class={styles.matches}
              style={floatingStyle()}
              onClick={(event) => {
                event.stopPropagation();
                const element =
                  event.target.closest<HTMLElement>('[role="button"]');
                if (element) {
                  setValue(element.dataset.title ?? "");
                }
              }}
              onKeyDown={(event) => {
                if (event.key === "Escape") {
                  closeFileMatches();
                  event.preventDefault();
                  fileRef()?.focus();
                  return;
                }

                if (event.key === "Tab") {
                  closeFileMatches();
                  event.preventDefault();

                  if (event.shiftKey) {
                    nameRef()?.focus();
                    return;
                  }

                  messageRef()?.focus();
                  return;
                }

                if (event.key === "Enter" || event.key === " ") {
                  event.preventDefault();
                  const focused = document.activeElement as HTMLElement;
                  if (focused.role !== "button") {
                    return;
                  }

                  setValue(focused.dataset.title ?? "");
                  return;
                }

                if (
                  event.target.role !== "button" ||
                  (event.key !== "ArrowDown" && event.key !== "ArrowUp")
                ) {
                  return;
                }

                event.preventDefault();
                const buttons = [
                  ...(fileMatchesRef()?.querySelectorAll<HTMLElement>(
                    'div[role="button"]',
                  ) ?? []),
                ];
                const index = buttons.indexOf(event.target as HTMLDivElement);

                if (event.key === "ArrowDown") {
                  buttons[Math.min(index + 1, buttons.length - 1)].focus();
                }

                if (event.key === "ArrowUp") {
                  if (index) {
                    buttons[index - 1].focus();
                  } else {
                    fileRef()?.focus();
                  }
                }
              }}
              onFocusOut={(event) => {
                if (
                  !(event.relatedTarget as HTMLElement | null)?.closest(
                    "#sr-file-matches",
                  ) &&
                  event.relatedTarget !== fileRef()
                ) {
                  closeFileMatches();
                }
              }}
            >
              <For each={matches()} fallback={<i>Nenhum resultado</i>}>
                {(match) => (
                  // biome-ignore lint/a11y/useFocusableInteractive: Safari workaround
                  // biome-ignore lint/a11y/useSemanticElements: Safari workaround
                  <div
                    role="button"
                    tabindex={-1}
                    data-title={formatTitle(match)}
                  >
                    <div class={styles["button-inner"]}>
                      <Image
                        displayFallback
                        class={styles.cover}
                        sources={[{ sizes: "any", src: match.cover }]}
                        alt=""
                      />
                      <div class={styles.meta}>
                        <Show when={match.title}>
                          <b>{match.title}</b>
                        </Show>
                        <Show when={match.artist}>
                          <span>{match.artist}</span>
                        </Show>
                        <Show when={match.album}>
                          <cite>{match.album}</cite>
                        </Show>
                      </div>
                    </div>
                  </div>
                )}
              </For>
            </div>
          </div>
          <div>
            <label for="sr-message">Recado</label>
            <textarea
              ref={setMessageRef}
              id="sr-message"
              name="message"
              minLength={10}
            />
          </div>
          <div>
            <Show when={sending()}>
              <IconTablerLoader2 class={rootStyles.spin} />
            </Show>
            <button type="submit">Enviar</button>
          </div>
        </fieldset>
      </form>
    </Modal>
  );
}
